import { Directive, computed, input, output, effect, model, OnChanges, SimpleChanges } from '@angular/core';
import { hlm } from '@spartan-ng/ui-core';
import { type VariantProps, cva } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

export const toggleVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:text-accent-foreground data-[state=on]:bg-accent ',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline: 'border border-input bg-transparent hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'h-9 px-3',
        sm: 'h-8 px-2',
        lg: 'h-10 px-3',
      },
      state: {
        on: '  bg-accent text-accent-foreground',
        off: ' text-[#4B4B4B]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      state: 'off',
    },
  }
);
type ToggleVariants = VariantProps<typeof toggleVariants> & {
  state: 'on' | 'off';
};

@Directive({
  selector: '[hlmToggle],[brnToggle][hlm]',
  standalone: true,
  host: {
    '[class]': '_computedClass()',
    '(click)': 'toggle({ state: isActive })',
  },
})
export class HlmToggleDirective implements OnChanges {
  public readonly variant = input<ToggleVariants['variant']>('default');
  public readonly size = input<ToggleVariants['size']>('default');
  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  public readonly activated = output();
  public readonly isActive = model<boolean>(false);

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    this.isActive.set(changes['isActive']?.currentValue ?? this.isActive());
  }

  toggle(object: any) {
    this.isActive.update((oldVal) => !oldVal);
    return this.activated.emit(object);
  }

  protected _computedClass = computed(() => {
    return hlm(
      toggleVariants({
        variant: this.variant(),
        size: this.size(),
        state: this.isActive() ? 'on' : 'off',
      }),
      this.userClass()
    );
  });
}
